import type {
  InvalidateQueryFilters,
  QueryClient} from '@tanstack/react-query';
import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import type {
  AddPowOfferProofsParams,
  GetHotOffersSearchParams} from '@api';
import {
  addPowOfferProof,
  GET_HOT_OFFERS_DEFAULT_LIMIT,
  getHotOfferPartnerStats,
  getHotOffers
} from '@api';

import { AUTH_USER_QUERY_KEY } from '@shared/hooks/query/keys';

import { hotOfferQueryKeys } from './hotOffer';

export const hotOffersQueryKeys = {
  infiniteList: (searchParams: GetHotOffersSearchParams = {}) => [
    'infiniteHotOffersList',
    searchParams,
  ],
  offerShareUrl: 'offerShareUrl',
  partnerStats: (limit: number) => [AUTH_USER_QUERY_KEY, 'partnerStats', limit],
} as const;

export const useHotOffersInfinityQuery = (
  params: Omit<GetHotOffersSearchParams, 'offset' | 'limit'>,
) => {
  return useInfiniteQuery({
    queryKey: ['infiniteHotOffersList', 'mode', params.mode ?? 'all'],
    queryFn: ({ pageParam: offset }) =>
      getHotOffers({
        searchParams: {
          ...params,
          offset,
        },
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, pages = []) =>
      lastPage?.data.length === GET_HOT_OFFERS_DEFAULT_LIMIT
        ? pages.length * GET_HOT_OFFERS_DEFAULT_LIMIT
        : undefined,
  });
};

export const prefetchHotOffersInfiniteQuery = async (
  clientQuery: QueryClient,
  params?: Parameters<typeof getHotOffers>[0],
) => {
  const queryKey = hotOffersQueryKeys.infiniteList(params?.searchParams);

  await clientQuery.prefetchInfiniteQuery({
    queryKey,
    queryFn: () =>
      getHotOffers({
        ...params,
        searchParams: {
          ...params?.searchParams,
          offset: 0,
        },
      }),
    initialPageParam: 0,
  });

  return clientQuery.getQueryData(queryKey);
};

export const useAddPowOfferProofMutation = (offerId?: string) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (params: AddPowOfferProofsParams) => {
      await addPowOfferProof({ json: params });
    },
    onSettled: () => {
      const queryKey = hotOfferQueryKeys.powHotOffer(offerId || '') as InvalidateQueryFilters;
      queryClient.invalidateQueries(queryKey);
    },
  });
};

export const usePartnerStatsInfiniteQuery = ({
  limit = 20,
  enabled,
}: {
  limit?: number;
  enabled?: boolean;
} = {}) => {
  return useInfiniteQuery({
    queryKey: hotOffersQueryKeys.partnerStats(limit),
    queryFn: ({ pageParam }) =>
      getHotOfferPartnerStats({ searchParams: { limit, offset: pageParam } }),
    getNextPageParam: (lastPage, pages = []) =>
      lastPage?.length === limit ? limit * pages.length : undefined,
    initialPageParam: 0,
    enabled,
    refetchInterval: 5000,
  });
};
